import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { DEOnly } from '@iwIntlProvider/CountryWrappers';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { Button, Text } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './ConfirmForm.module.css';
import { getDrawdownNextRepaymentDate } from '../../../../api/repaymentScheduleSelector';
import { getDrawdownType } from '../../../drawDown/getDrawdownType';
import NextRepayment from '../../../funding/ConfirmFundingView/confirmForm/nextRepayment/NextRepayment';
import { SHOW_REPAYMENT_SCHEDULE_MODAL } from '../../../modals/repaymentScheduleModal/RepaymentScheduleModal.actions';
import { MainSectionWrapper } from '../../../pageStructure/mainSectionWrapper';
import PairItemContainer from '../../../ui/pairItemContainer/PairItemContainer';
import { CONFIRM_FUNDING } from '../../Funding.actions';

const mapStateToProps = (state: RootState) => ({
    nextRepaymentDate: getDrawdownNextRepaymentDate(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    confirm: (offerId: string) =>
        dispatch({
            type: CONFIRM_FUNDING,
            amount: 0,
            offerId
        }),
    showSchedule: () => dispatch({ type: SHOW_REPAYMENT_SCHEDULE_MODAL })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

type TProps = {
    formalOffer: TFormalOffer;
};

const RCFCashlessActivation = ({
    formalOffer,
    nextRepaymentDate
}: {
    formalOffer: TFormalOffer;
    nextRepaymentDate: string | null;
}) => {
    const creditLimit = formalOffer.max_amount!;
    const fixedFee =
        formalOffer.proposed_access_period_details!
            .access_period_opening_fee_max_amount!;

    return (
        <PairItemContainer>
            <div className="space-y-s p-m min-l:p-2xl">
                <Text className="text-l font-med">
                    <FormattedMessage
                        id="_.confirm_funding.rcf_cashless.credit_line_label"
                        defaultMessage="Available Credit Line:"
                    />
                </Text>
                <Text className="text-4xl font-bold">
                    <FormattedCurrency value={creditLimit} />
                </Text>
                <Text className="text-s font-med">
                    <FormattedMessage
                        id="_.confirm_funding.rcf_cashless.credit_line_description"
                        defaultMessage="You can withdraw funds at any time within this limit."
                    />
                </Text>
            </div>
            <div className="space-y-s p-m min-l:p-2xl">
                <Text className="text-l font-med">
                    <FormattedMessage
                        id="_.confirm_funding.rcf_cashless.fixed_fee_label"
                        defaultMessage="Fixed Fee:"
                    />
                </Text>
                <Text className="text-4xl font-bold">
                    <FormattedCurrency value={fixedFee} />
                </Text>
                <Text className="text-s font-med">
                    <FormattedMessage
                        id="_.confirm_funding.rcf_cashless.fixed_fee_description"
                        defaultMessage="Will be deducted with the first repayment on {date}."
                        values={{
                            date: (
                                <FormattedDate
                                    value={new Date(nextRepaymentDate!)}
                                    day="numeric"
                                    month="long"
                                    year="numeric"
                                />
                            )
                        }}
                    />
                </Text>
            </div>
        </PairItemContainer>
    );
};

const CashlessConfirmContent = ({
    drawdownType,
    showSchedule
}: {
    drawdownType: string;
    showSchedule: () => void;
}) => (
    <PairItemContainer>
        <div className={styles.selectedAmount}>
            <div className={styles.summaryTitle}>
                {drawdownType === 'repayment_reduction' ? (
                    <FormattedMessage
                        id="_.confirm_funding.top_up.repayment_reduction_summary_title"
                        defaultMessage="You’ll reduce your monthly repayments by extending your loan. Your outstanding balance will not be affected."
                    />
                ) : (
                    <FormattedMessage
                        id="_.confirm_funding.top_up.interestOnly_summary_title"
                        defaultMessage="You are extending your interest-only period"
                    />
                )}
            </div>
        </div>

        <div className={styles.repayments}>
            <NextRepayment />
            <div className={styles.showSchedule}>
                <span className={styles.link} onClick={showSchedule}>
                    <FormattedMessage
                        id="_.confirm_funding.repayment_breakdown"
                        defaultMessage="See full repayment schedule"
                    />
                </span>
            </div>
        </div>
    </PairItemContainer>
);

const Confirm = ({
    confirm,
    showSchedule,
    formalOffer,
    nextRepaymentDate
}: TProps & PropsFromRedux) => {
    const drawdownType = getDrawdownType(formalOffer);
    const isRCFCashlessActivation =
        drawdownType === 'revolving_credit_facility_cashless_activation';

    return (
        <div className={styles.Confirm}>
            <MainSectionWrapper className="!pb-0">
                {isRCFCashlessActivation ? (
                    <RCFCashlessActivation
                        formalOffer={formalOffer}
                        nextRepaymentDate={nextRepaymentDate}
                    />
                ) : (
                    <CashlessConfirmContent
                        drawdownType={drawdownType}
                        showSchedule={showSchedule}
                    />
                )}
            </MainSectionWrapper>
            <div className={styles.newFundingConfirmButton}>
                <div className="mx-auto max-w-[550px] text-center">
                    <Button
                        variant="success"
                        onClick={() => confirm(formalOffer.offer_id!)}
                        size="large"
                        data-action-name="Confirm funding"
                    >
                        {isRCFCashlessActivation ? (
                            <FormattedMessage
                                id="_.confirm_funding.rcf_cashless.activate"
                                defaultMessage="Activate Credit Line"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.confirm_funding.confirm"
                                defaultMessage="Confirm"
                            />
                        )}
                    </Button>
                    <DEOnly>
                        <Text className="mt-s text-s">
                            <FormattedMessage
                                id="_.confirm_funding.cashless.voluntary_takeup_disclaimer"
                                defaultMessage="By confirming, you declare that you have independently chosen the loan offered to you and had the opportunity to obtain offers from other lenders."
                            />
                        </Text>
                    </DEOnly>
                </div>
            </div>
        </div>
    );
};

export const CashlessConfirmForm = connect(
    mapStateToProps,
    mapDispatchToProps
)(Confirm);
